export type Lang = "en" | "fr" | "de" | "es" | "it";

export const LANGS: Lang[] = ["en", "fr", "de", "es", "it"];

export const LANG_LABELS: Record<Lang, string> = {
  en: "EN",
  fr: "FR",
  de: "DE",
  es: "ES",
  it: "IT",
};

export const CHATEAU_URL = "https://chateau.let.wine/";
export const QUIZ_URL = "https://quiz.let.wine/";
export const CANVA_URL = "https://letwine.my.canva.site/";

export interface Translation {
  htmlLang: string;
  nav: {
    chateau: string;
    parties: string;
    personal: string;
    about: string;
    cta: string;
  };
  hero: {
    h1: string;
    sub: string;
    cta: string;
  };
  sections: {
    chateau: { title: string; desc: string; cta: string };
    parties: { title: string; desc: string; cta: string };
    personal: { title: string; desc: string; cta: string };
    about: { title: string; desc: string; cta: string };
  };
  footer: {
    tagline: string;
    copyright: string;
  };
}

const en: Translation = {
  htmlLang: "en",
  nav: {
    chateau: "Château",
    parties: "Parties",
    personal: "For me",
    about: "About",
    cta: "Try now",
  },
  hero: {
    h1: "Let.Wine",
    sub: "The wine that matches you. Discover wines tailored to your taste through our château quiz, personal selection, and wines to share with friends.",
    cta: "Find my wine",
  },
  sections: {
    chateau: {
      title: "Château Saint-Julien d'Aille",
      desc: "Discover the château wines that match your taste. Take the quiz and get a personalised selection.",
      cta: "Take the quiz",
    },
    parties: {
      title: "Let.Wine Parties",
      desc: "Your guests' tastes, a shopping list for you. Wines available in stores across France.",
      cta: "Plan a party",
    },
    personal: {
      title: "Let.Wine for me",
      desc: "Your personal wine selection, available in stores across France. Curated just for you.",
      cta: "My selection",
    },
    about: {
      title: "About Let.Wine",
      desc: "Learn more about the Let.Wine project and our mission to help everyone find their perfect wine.",
      cta: "Learn more",
    },
  },
  footer: {
    tagline: "À chacun son vin.",
    copyright: "© 2026 Let.Wine. All rights reserved.",
  },
};

const fr: Translation = {
  htmlLang: "fr",
  nav: {
    chateau: "Château",
    parties: "Soirées",
    personal: "Pour moi",
    about: "À propos",
    cta: "Essayer",
  },
  hero: {
    h1: "Let.Wine",
    sub: "Le vin qui vous ressemble. Découvrez les vins qui correspondent à vos goûts grâce au quiz du Château, une sélection personnelle et des vins à partager entre amis.",
    cta: "Trouver mon vin",
  },
  sections: {
    chateau: {
      title: "Château Saint-Julien d'Aille",
      desc: "Découvrez les vins du Château qui correspondent à vos goûts. Répondez au quiz et obtenez une sélection personnalisée.",
      cta: "Faire le quiz",
    },
    parties: {
      title: "Let.Wine Parties",
      desc: "Les goûts de vos invités, une liste d'achat pour vous. Des vins disponibles en magasins en France.",
      cta: "Organiser une soirée",
    },
    personal: {
      title: "Let.Wine pour moi",
      desc: "Votre sélection personnelle de vins, à retrouver dans les magasins en France. Une sélection rien que pour vous.",
      cta: "Ma sélection",
    },
    about: {
      title: "À propos de Let.Wine",
      desc: "Découvrez le projet Let.Wine et notre mission : aider chacun à trouver le vin qui lui correspond.",
      cta: "En savoir plus",
    },
  },
  footer: {
    tagline: "À chacun son vin.",
    copyright: "© 2026 Let.Wine. Tous droits réservés.",
  },
};

const de: Translation = {
  htmlLang: "de",
  nav: {
    chateau: "Château",
    parties: "Partys",
    personal: "Für mich",
    about: "Über uns",
    cta: "Ausprobieren",
  },
  hero: {
    h1: "Let.Wine",
    sub: "Der Wein, der zu Ihnen passt. Entdecken Sie Weine, die Ihrem Geschmack entsprechen — mit dem Château-Quiz, einer persönlichen Auswahl und Weinen für gemeinsame Abende.",
    cta: "Meinen Wein finden",
  },
  sections: {
    chateau: {
      title: "Château Saint-Julien d'Aille",
      desc: "Entdecken Sie die Weine des Château, die Ihrem Geschmack entsprechen. Machen Sie das Quiz und erhalten Sie eine persönliche Auswahl.",
      cta: "Quiz machen",
    },
    parties: {
      title: "Let.Wine Parties",
      desc: "Der Geschmack Ihrer Gäste, eine Einkaufsliste für Sie. Weine, die in Geschäften in Frankreich erhältlich sind.",
      cta: "Party planen",
    },
    personal: {
      title: "Let.Wine für mich",
      desc: "Ihre persönliche Weinauswahl, erhältlich in Geschäften in Frankreich. Eine Auswahl, die nur für Sie kuratiert wurde.",
      cta: "Meine Auswahl",
    },
    about: {
      title: "Über Let.Wine",
      desc: "Erfahren Sie mehr über das Let.Wine-Projekt und unsere Mission: jedem helfen, seinen perfekten Wein zu finden.",
      cta: "Mehr erfahren",
    },
  },
  footer: {
    tagline: "À chacun son vin.",
    copyright: "© 2026 Let.Wine. Alle Rechte vorbehalten.",
  },
};

const es: Translation = {
  htmlLang: "es",
  nav: {
    chateau: "Château",
    parties: "Fiestas",
    personal: "Para mí",
    about: "Acerca de",
    cta: "Probar",
  },
  hero: {
    h1: "Let.Wine",
    sub: "El vino que te va. Descubre vinos a tu medida con el quiz del Château, una selección personal y vinos para compartir con amigos.",
    cta: "Encontrar mi vino",
  },
  sections: {
    chateau: {
      title: "Château Saint-Julien d'Aille",
      desc: "Descubre los vinos del Château que se adaptan a tu gusto. Haz el quiz y obtén una selección personalizada.",
      cta: "Hacer el quiz",
    },
    parties: {
      title: "Let.Wine Parties",
      desc: "Los gustos de tus invitados, una lista de la compra para ti. Vinos disponibles en tiendas de Francia.",
      cta: "Organizar una fiesta",
    },
    personal: {
      title: "Let.Wine para mí",
      desc: "Tu selección personal de vinos, disponible en tiendas de Francia. Una selección curada solo para ti.",
      cta: "Mi selección",
    },
    about: {
      title: "Acerca de Let.Wine",
      desc: "Conoce el proyecto Let.Wine y nuestra misión: ayudar a cada persona a encontrar su vino perfecto.",
      cta: "Saber más",
    },
  },
  footer: {
    tagline: "À chacun son vin.",
    copyright: "© 2026 Let.Wine. Todos los derechos reservados.",
  },
};

const it: Translation = {
  htmlLang: "it",
  nav: {
    chateau: "Château",
    parties: "Feste",
    personal: "Per me",
    about: "Chi siamo",
    cta: "Prova",
  },
  hero: {
    h1: "Let.Wine",
    sub: "Il vino che ti somiglia. Scopri i vini su misura per te con il quiz del Château, una selezione personale e vini da condividere con gli amici.",
    cta: "Trova il mio vino",
  },
  sections: {
    chateau: {
      title: "Château Saint-Julien d'Aille",
      desc: "Scopri i vini del Château che corrispondono ai tuoi gusti. Fai il quiz e ottieni una selezione personalizzata.",
      cta: "Fai il quiz",
    },
    parties: {
      title: "Let.Wine Parties",
      desc: "I gusti dei tuoi ospiti, una lista della spesa per te. Vini disponibili nei negozi in Francia.",
      cta: "Organizza una festa",
    },
    personal: {
      title: "Let.Wine per me",
      desc: "La tua selezione personale di vini, disponibile nei negozi in Francia. Una selezione curata solo per te.",
      cta: "La mia selezione",
    },
    about: {
      title: "Chi è Let.Wine",
      desc: "Scopri il progetto Let.Wine e la nostra missione: aiutare ognuno a trovare il proprio vino perfetto.",
      cta: "Scopri di più",
    },
  },
  footer: {
    tagline: "À chacun son vin.",
    copyright: "© 2026 Let.Wine. Tutti i diritti riservati.",
  },
};

export const translations: Record<Lang, Translation> = { en, fr, de, es, it };
